import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  Table, TableBody, TableCell, TableRow, TextField, Typography, styled,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { palette } from '../../theme/palette';
import useRole from '../../hooks/useRole';
import { DeleteIconNoBorder } from '../Icons/DeleteIcon';
import { DistrictResourcesCount } from '../../generated/graphql';

const styles = {
  icon: {
    marginRight: '8px',
    fill: palette.customBlue.primaryBlue,
    '& path': {
      fill: palette.customBlue.primaryBlue,
    },
  },
  iconDisabled: {
    marginRight: '8px',
    fill: palette.customBackground.disabled,
    '& path': {
      fill: palette.customBackground.disabled,
    },
  },
  button: {
    fontWeight: 800,
    minWidth: '180px',
    maxHeight: '50px',
    py: 1.5, px: 2,
    mx: 1,
  },
  buttonDisabled: {
    backgroundColor: palette.customWhite.main,
    color: palette.customBackground.disabled,
  },
  deleteButtonIcon: {
    backgroundColor: palette.customRed.deleteButton,
    color: palette.customWhite.main,
    '&:hover': {
      backgroundColor: palette.customRed.deleteButtonShadow,
      color: palette.customWhite.main,
    },
    '&.MuiButton-root::after': {
      backgroundColor: palette.customRed.deleteButton,
    },
  },
  inputInput: {
    '& .MuiInputBase-input': {
      background: 'none',
      border: 0,
    },
  },
};

export const StyledDeleteButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.customRed.deleteButton,
  color: theme.palette.customWhite.main,
  '&::after': {
    borderColor: theme.palette.customRed.deleteButton,
  },
  '&:hover': {
    color: theme.palette.customWhite.main,
    backgroundColor: theme.palette.customRed.deleteButton,
  },
}));

export const StyledCircleBtnIcon = styled(Button)<{ buttonHeight?: number, buttonWidth?: number, padding?: number }>(
  ({ buttonHeight, buttonWidth, padding }) => ({
    minWidth: 50,
    borderRadius: '50%',
    '&::after': {
      borderRadius: '50%',
    },
    height: buttonHeight ? buttonHeight : 50,
    width: buttonWidth ? buttonWidth : 50,
    ...(padding !== undefined ? { padding: padding } : {} ),
    '&:active': {
      borderRadius: '50%',
    },
  }));

interface LinkProps {
  pathname: string,
  state: any,
}
interface Props {
  title: string,
  primaryBtnLabel?: string,
  PrimaryBtnIcon?: any,
  primaryBtnRedirectionLink?: string | LinkProps
  secondaryBtnLabel?: string,
  SecondaryBtnIcon?: any,
  secondaryBtnRedirectionLink?: string,
  secondaryBtnOnClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void,
  addNewEntity?: React.ReactNode,
  syncNowRedirectionLink?: string,
  syncNowEnabled?: Boolean | null,
  syncNowShow?: Boolean | null,
  handleSecondryBtnRedirectionClick?: Function,
  districtID?: string,
  resourceCountByDistrict?: DistrictResourcesCount,
  deleteDistrictShow?: boolean,
  deleteDistrictDisable?: boolean,
  deleteDistrictRequestHandler?: Function,
  deleteDistrictRequestOpen?: boolean,
}

const ResourceDetailHeader = ({
  title,
  primaryBtnLabel,
  PrimaryBtnIcon,
  primaryBtnRedirectionLink,
  secondaryBtnLabel,
  SecondaryBtnIcon,
  secondaryBtnRedirectionLink,
  secondaryBtnOnClick,
  addNewEntity,
  syncNowRedirectionLink,
  syncNowEnabled = false,
  syncNowShow = false,
  handleSecondryBtnRedirectionClick = () => { },
  districtID,
  resourceCountByDistrict,
  deleteDistrictShow = false,
  deleteDistrictDisable = false,
  deleteDistrictRequestHandler, deleteDistrictRequestOpen,
}: Props) => {

  const { isSuperAdmin } = useRole();
  const [deleteDistrictOpen, setDeleteDistrictOpen] = useState(false);
  const [deleteConfirmInputMsg, setDeleteConfirmInputMsg] = useState('');
  const [deleteConfirmInputErrMsg, setDeleteConfirmInputErrMsg] = useState('');
  const deleteDistrictConfirmInputErr = 'Sorry, you must enter the text exactly to confirm.';

  const toggleDeleteDistrictOpen = () => {
    setDeleteDistrictOpen(!deleteDistrictOpen);
  };

  const handleDeleteDistrictOpen = () => {
    toggleDeleteDistrictOpen();
  };

  const handleDeleteDistrictClose = () => {
    setDeleteConfirmInputErrMsg('');
    toggleDeleteDistrictOpen();
  };

  const handleDeleteDistrictConfirm = async () => {
    if (deleteConfirmInputMsg === '' || deleteConfirmInputErrMsg) {
      setDeleteConfirmInputErrMsg(deleteDistrictConfirmInputErr);
      document.getElementById('delete-district-confirm')?.focus();
    } else {
      await deleteDistrictRequestHandler?.(districtID);
      setDeleteConfirmInputMsg('');
      toggleDeleteDistrictOpen();
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={5}>
        <Typography variant="pageTitle" sx={{ wordBreak: 'break-word' }}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={7} container justifyContent='flex-end'>
        {(isSuperAdmin && deleteDistrictShow) && (
          <>
            {
              !Boolean(deleteDistrictDisable) && <StyledDeleteButton
                sx={{ ...styles.button }}
                onClick={handleDeleteDistrictOpen}
              >
              Delete District
              </StyledDeleteButton>
            }
            <Dialog
              open={deleteDistrictOpen}
              onClose={toggleDeleteDistrictOpen}
              PaperProps={{
                sx: {
                  borderTop: `12px solid ${palette.customRed.deleteButton}`,
                },
              }}
            >
              <DialogTitle sx={{ paddingTop: 0, textAlign: 'center' }} >
                <Box>
                  <StyledCircleBtnIcon
                    variant="contained"
                    buttonWidth={70}
                    buttonHeight={70}
                    padding={0}
                    sx={{
                      ...styles.deleteButtonIcon,
                    }}
                  >
                    <DeleteIconNoBorder
                      strokeColor={palette.customRed.deleteButton} strokeWidth={2.5}
                      svgWidth='2em' svgHeight='1.5em'
                      viewBox='0 0 18 22'
                    />
                  </StyledCircleBtnIcon>
                </Box>
                <Box sx={{ marginTop: 2 }}>
                  <Typography variant='modalTitle1'>
                    Delete {title} District?
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Grid
                  container
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box width="80%" textAlign='center'>
                    <Typography variant='tableTitleData' sx={{ fontWeight: 600 }}>
                      You’ll permanently lose all data associated with this district:
                    </Typography>
                  </Box>
                  <Table aria-label="simple table" sx={{ width: '80%' }}>
                    <TableBody>
                      <TableRow
                        key='schools-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">Schools</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.school_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='classes-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">Classes</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.class_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='district-admin-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">District Admins</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.district_admin_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='school-admin-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">School Admins</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.school_admin_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='teacher-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">Teachers</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.teacher_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='student-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">Students</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.student_count}</TableCell>
                      </TableRow>
                      <TableRow
                        key='sync-count'
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left">Syncs</TableCell>
                        <TableCell align="left">{resourceCountByDistrict?.sync_count}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Typography variant='tableTitleData' sx={{ fontWeight: 700, marginTop: 4 }}>
                    Type “delete district” to confirm
                  </Typography>
                  <Box width="80%" textAlign='center'>
                    <TextField
                      id="delete-district-confirm"
                      name="delete-district-confirm"
                      aria-describedby="delete-district-confirm"
                      fullWidth
                      sx={{ ...styles.inputInput }}
                      autoComplete="off"
                      onChange={(e: any) => {
                        !e.target.value || e.target.value === 'delete district' ?
                          setDeleteConfirmInputErrMsg('') :
                          setDeleteConfirmInputErrMsg(deleteDistrictConfirmInputErr);
                        setDeleteConfirmInputMsg(e.target.value);
                      }}
                    />
                    {deleteConfirmInputErrMsg && (
                      <Typography variant='sFormHelper'>
                        {deleteConfirmInputErrMsg}
                      </Typography>
                    )}
                  </Box>
                  <DialogActions sx={{ marginTop: 4, paddingX: 0 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      sx={{ ...styles.button }}
                      onClick={handleDeleteDistrictClose}
                    >
                      Cancel
                    </Button>
                    <StyledDeleteButton
                      sx={{ ...styles.button }}
                      onClick={handleDeleteDistrictConfirm}
                    >
                      Delete District
                    </StyledDeleteButton>
                  </DialogActions>
                </Grid>
              </DialogContent>
            </Dialog>
          </>
        )}
        {syncNowRedirectionLink && isSuperAdmin && syncNowShow && (
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            sx={{ ...styles.button }}
            to={syncNowRedirectionLink}
            disabled={Boolean(syncNowEnabled) || deleteDistrictRequestOpen}
          >
            Sync Now
          </Button>
        )}
        {secondaryBtnLabel && (secondaryBtnOnClick ? (
          <Button
            variant="outlined"
            color="primary"
            sx={{ ...styles.button }}
            onClick={secondaryBtnOnClick || (() => {})}
            startIcon={SecondaryBtnIcon ? <SecondaryBtnIcon  sx={{ ...styles.icon }} /> : undefined}
          >
            {secondaryBtnLabel}
          </Button>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            sx={{ ...styles.button }}
            to={secondaryBtnRedirectionLink ?? ''}
            startIcon={SecondaryBtnIcon ? <SecondaryBtnIcon sx={{ ...styles.icon }} /> : undefined}
            onClick={() => handleSecondryBtnRedirectionClick()}
          >
            {secondaryBtnLabel}
          </Button>
        ))}
        {addNewEntity && <>{addNewEntity}</>}
        {primaryBtnLabel && PrimaryBtnIcon && primaryBtnRedirectionLink && (
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            sx={{ ...styles.button }}
            to={primaryBtnRedirectionLink}
            startIcon={<PrimaryBtnIcon sx={{ ...(deleteDistrictOpen && styles.icon) }} fillColor={deleteDistrictRequestOpen ? palette.customBackground.disabled : palette.customBlue.primaryBlue} />}
            disabled={deleteDistrictRequestOpen}
          >
            {primaryBtnLabel}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default ResourceDetailHeader;
